import dayjs, { Dayjs } from "dayjs";
import { ListReturnsActionKind, ListReturnsActions } from "./action";
import { ListReturnsState, totalPageCount } from "./common";
import { getStatusTabs } from "./selector";
import { DealerAccount as DealerAccountType } from "../../../../api/dealer/types";
import { isCreateReturnsEnabled } from "../../../../utils/showReturns";
import { AllReturnsState } from "../allReturnsGrid/AllReturnsGrid";
import { FilterWithInfo, ReturnTypeEnum } from "../allReturnsGrid/types";

export enum FilterType {
  ReturnId = "returnId",
  ReturnType = "returnType",
  DealerReference = "dealerReference",
  InvoiceNumber = "invoiceNumber",
  PartNumber = "partNumber",
  NotaFiscalNumber = "NotaFiscalNumber",
  DealerAccount = "dealerAccount",
}

export type FeatureToggles = {
  isSurplusReturnsEnabled: boolean;
  isRBACEnabled: boolean;
  isCreateReturnEnabled: boolean;
  isReturnsEnabled: boolean;
  isCoreReturnsEnabled: boolean;
  isVendorDirectReturnEnabled: boolean;
  isLcpOptimisationReturnsListEnabled?: boolean;
  isDealerPreparationEnabled: boolean;
  isDailyScrapCreditPendingEnabled?: boolean;
  isJdpsDailyReturnActivitiesEnabled?: boolean;
  isJdpsSurplusReturnActivitiesEnabled?: boolean;
  isJdpsDealer?: boolean;
};

export const getInitialState = (
  accountId: string,
  language: string,
  currentDate: Dayjs,
  featureToggles: FeatureToggles,
  selectedAccounts: DealerAccountType[],
  authState?: { isAuthenticated?: boolean; accessToken?: string },
  dealerLocation: string = "",
  allReturnsState?: AllReturnsState,
  isUserAuthorizedToCreateReturn: boolean = false,
  isUserAuthorizedToEditReturn: boolean = false,
  isUserAuthorizedForMultipleAccounts: boolean = false
): ListReturnsState => {
  const filterWithInfo = getInitialStateFilterWithInfo(
    currentDate,
    featureToggles.isSurplusReturnsEnabled,
    dealerLocation,
    featureToggles.isJdpsDailyReturnActivitiesEnabled ?? false,
    featureToggles.isJdpsSurplusReturnActivitiesEnabled ?? false
  );
  return {
    accountId,
    authState,
    returns: [],
    statusCount: {},
    tabIndex: allReturnsState?.tabIndex || 0,
    isLoading: false,
    language,
    isReturnsLoaded: false,
    showCancelModal: false,
    returnAttributesToCancel: {
      returnId: "",
      returnTypeCode: "",
      returnStatusList: [],
    },
    filters: getFilterWithInfoFromAllReturnsState(
      allReturnsState,
      filterWithInfo
    ),
    pageNumber: allReturnsState?.pageNumber || 0,
    pageSize: allReturnsState?.pageSize || 10,
    isFilterApplied: false,
    showCreateReturns: isCreateReturnsEnabled(),
    dealerLocation,
    sortField: allReturnsState?.sortField || "returnDate",
    sortDir: allReturnsState?.sortDir || "desc",
    featureToggles,
    selectedAccounts,
    isUserAuthorizedToCreateReturn,
    isUserAuthorizedToEditReturn,
    isUserAuthorizedForMultipleAccounts,
  };
};

const getFilterWithInfoFromAllReturnsState = (
  allReturnsState: AllReturnsState | undefined,
  filterWithInfo: FilterWithInfo
): FilterWithInfo => {
  const filters = allReturnsState?.filters;
  if (
    filters &&
    filters.startDate instanceof dayjs &&
    filters.endDate instanceof dayjs &&
    filters.minDate instanceof dayjs
  ) {
    return filters;
  }
  return filterWithInfo;
};

const getInitialStateFilterWithInfo = (
  currentDate: dayjs.Dayjs,
  isSurplusReturnsEnabled: boolean,
  dealerLocation: string,
  isJdpsDailyReturnActivitiesEnabled: boolean = false,
  isJdpsSurplusReturnActivitiesEnabled: boolean = false
): FilterWithInfo => ({
  returnId: null,
  returnType: getReturnType(isSurplusReturnsEnabled),
  returnTypeList: getReturnTypeList(
    isSurplusReturnsEnabled,
    dealerLocation,
    isJdpsDailyReturnActivitiesEnabled,
    isJdpsSurplusReturnActivitiesEnabled
  ),
  dealerAccount: null,
  dealerReference: null,
  invoiceNumber: null,
  nfeNumber: null,
  partNumber: null,
  startDate: currentDate.add(1, "day").subtract(3, "months"),
  endDate: currentDate,
  minDate: currentDate.add(1, "day").subtract(12, "months"),
});

const getReturnTypeList = (
  isSurplusReturnsEnabled: boolean,
  dealerLocation: string,
  isJdpsDailyReturnActivitiesEnabled: boolean = false,
  isJdpsSurplusReturnActivitiesEnabled: boolean = false
): ReturnTypeEnum[] => {
  if (isSurplusReturnsEnabled) {
    if (
      isJdpsDailyReturnActivitiesEnabled ||
      isJdpsSurplusReturnActivitiesEnabled
    ) {
      return [
        ReturnTypeEnum.ALL,
        ...(isJdpsDailyReturnActivitiesEnabled ? [ReturnTypeEnum.ZCRR] : []),
        ...(isJdpsSurplusReturnActivitiesEnabled ? [ReturnTypeEnum.ZSPR] : []),
      ];
    }
    return dealerLocation === "BR"
      ? [
          ReturnTypeEnum.ALL,
          ReturnTypeEnum.ZCRR,
          ReturnTypeEnum.ZSPR,
          ReturnTypeEnum.NMQ,
        ]
      : [ReturnTypeEnum.ALL, ReturnTypeEnum.ZCRR, ReturnTypeEnum.ZSPR];
  }
  return [ReturnTypeEnum.ZCRR];
};

const getReturnType = (isSurplusReturnsEnabled: boolean): string => {
  if (isSurplusReturnsEnabled) {
    return ReturnTypeEnum.ALL.toString();
  }
  return ReturnTypeEnum.ZCRR.toString();
};

export function listReturnsReducer(
  state: ListReturnsState,
  action: ListReturnsActions
): ListReturnsState {
  switch (action.type) {
    case ListReturnsActionKind.SET_RETURNS:
      return {
        ...state,
        returns: action.payload.returns,
        statusCount: action.payload.statusCount ?? state.statusCount,
      };
    case ListReturnsActionKind.SET_TAB_INDEX: {
      return {
        ...state,
        tabIndex: action.payload,
        pageNumber: 0,
      };
    }
    case ListReturnsActionKind.SET_IS_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    case ListReturnsActionKind.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload,
      };
    case ListReturnsActionKind.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case ListReturnsActionKind.SET_IS_RETURNS_LOADED:
      return {
        ...state,
        isReturnsLoaded: action.payload,
      };
    case ListReturnsActionKind.SET_SHOW_CANCEL_MODAL:
      return {
        ...state,
        showCancelModal: action.payload,
      };
    case ListReturnsActionKind.SET_RETURN_ATTRIBUTES_TO_CANCEL:
      return {
        ...state,
        returnAttributesToCancel: action.payload,
      };
    case ListReturnsActionKind.SET_SORT_PARAMS_ACTION:
      if (
        state.sortDir === action.payload.sort &&
        state.sortField === action.payload.field
      )
        return state;
      return {
        ...state,
        sortField: action.payload.field,
        sortDir: action.payload.sort,
      };
    case ListReturnsActionKind.CLEAR_FILTER:
      return {
        ...state,
        filters: getClearStateFilterWithInfo(state, action.payload),
      };
    case ListReturnsActionKind.SET_FILTER: {
      const updatedFilter = setFilters(
        state,
        action.payload.value,
        action.payload.type
      );

      return {
        ...state,
        tabIndex: getTabIndex(updatedFilter, state),
        filters: updatedFilter,
      };
    }
    case ListReturnsActionKind.SET_PAGE_ROWS_SIZE:
      return {
        ...state,
        pageSize: action.payload,
        pageNumber: 0,
      };
    case ListReturnsActionKind.PREVIOUS_PAGE:
      return {
        ...state,
        pageNumber:
          state.pageNumber === 0 ? state.pageNumber : state.pageNumber - 1,
      };
    case ListReturnsActionKind.NEXT_PAGE:
      return {
        ...state,
        pageNumber:
          state.pageNumber === totalPageCount(state) - 1
            ? state.pageNumber
            : state.pageNumber + 1,
      };
    case ListReturnsActionKind.FIRST_PAGE:
      return {
        ...state,
        pageNumber: 0,
      };
    case ListReturnsActionKind.LAST_PAGE:
      return {
        ...state,
        pageNumber: totalPageCount(state) - 1,
      };
    case ListReturnsActionKind.SET_IS_FILTER_APPLIED:
      return {
        ...state,
        isFilterApplied: action.payload,
      };
    case ListReturnsActionKind.SET_DATE_RANGE_FILTER_ACTION: {
      return {
        ...state,
        filters: {
          ...state.filters,
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
        },
      };
    }
    case ListReturnsActionKind.UPDATE_SELECTED_ACCOUNTS: {
      return {
        ...state,
        selectedAccounts: action.payload.selectedAccounts,
        featureToggles: {
          ...state.featureToggles,
          isSurplusReturnsEnabled: action.payload.isSurplusReturnsEnabled,
        },
        returns: [],
        statusCount: {},
        tabIndex: 0,
        isReturnsLoaded: false,
        pageNumber: 0,
        pageSize: 10,
        filters: getInitialStateFilterWithInfo(
          dayjs(),
          state.featureToggles.isSurplusReturnsEnabled,
          state.dealerLocation,
          state.featureToggles.isJdpsDailyReturnActivitiesEnabled
        ),
        isFilterApplied: false,
      };
    }
    case ListReturnsActionKind.CLEAN_FILTERS: {
      const updatedFilter = getCleanedFilterValues(state);
      return {
        ...state,
        filters: updatedFilter,
      };
    }
    default:
      return state;
  }
}

const getTabIndex = (
  updatedFilter: FilterWithInfo,
  state: ListReturnsState
): number => {
  const statusTabs = getStatusTabs(state);

  const statusSelected = statusTabs[state.tabIndex];
  const newStatusTabs = getStatusTabs({
    ...state,
    filters: {
      ...updatedFilter,
    },
  });
  const tabIndex = newStatusTabs.findIndex(
    (statusTab) => statusTab === statusSelected
  );
  return tabIndex === -1 ? 0 : tabIndex;
};

const setFilters = (
  state: ListReturnsState,
  value: string,
  type: string
): FilterWithInfo => {
  const updatedValue = value.replace(/[[\]]/g, ""); // to prevent search API failure

  switch (type) {
    case FilterType.ReturnId:
      return { ...state.filters, returnId: updatedValue };
    case FilterType.DealerReference:
      return {
        ...state.filters,
        dealerReference: updatedValue,
      };
    case FilterType.ReturnType:
      return {
        ...state.filters,
        returnType: value,
        invoiceNumber: value === "ZSPR" ? "" : state.filters.invoiceNumber,
        nfeNumber: value === "ZSPR" ? "" : state.filters.nfeNumber,
      };
    case FilterType.PartNumber:
      return { ...state.filters, partNumber: updatedValue };
    case FilterType.InvoiceNumber:
      return {
        ...state.filters,
        returnType: "ZCRR",
        returnTypeList: [ReturnTypeEnum.ZCRR],
        invoiceNumber: updatedValue,
      };
    case FilterType.NotaFiscalNumber:
      return {
        ...state.filters,
        returnType: "ZCRR",
        returnTypeList: [ReturnTypeEnum.ZCRR],
        nfeNumber: updatedValue,
      };
    case FilterType.DealerAccount:
      return { ...state.filters, dealerAccount: updatedValue };
    default:
      return state.filters;
  }
};

const getClearStateFilterWithInfo = (
  state: ListReturnsState,
  type: string
): FilterWithInfo => {
  switch (type) {
    case FilterType.ReturnId:
      return {
        ...state.filters,
        returnId: null,
      };
    case FilterType.DealerReference:
      return {
        ...state.filters,
        dealerReference: null,
      };
    case FilterType.PartNumber:
      return {
        ...state.filters,
        partNumber: null,
      };
    case FilterType.InvoiceNumber:
      if (!state.filters.nfeNumber) {
        return {
          ...state.filters,
          returnTypeList: getReturnTypeList(
            state.featureToggles.isSurplusReturnsEnabled,
            state.dealerLocation,
            state.featureToggles.isJdpsDailyReturnActivitiesEnabled
          ),
          invoiceNumber: null,
        };
      }
      return {
        ...state.filters,
        invoiceNumber: null,
      };
    case FilterType.NotaFiscalNumber:
      if (!state.filters.invoiceNumber) {
        return {
          ...state.filters,
          returnTypeList: getReturnTypeList(
            state.featureToggles.isSurplusReturnsEnabled,
            state.dealerLocation,
            state.featureToggles.isJdpsDailyReturnActivitiesEnabled
          ),
          nfeNumber: null,
        };
      }
      return {
        ...state.filters,
        nfeNumber: null,
      };
    case FilterType.DealerAccount:
      return {
        ...state.filters,
        dealerAccount: null,
      };
    default:
      return state.filters;
  }
};

const getCleanedFilterValues = (state: ListReturnsState): FilterWithInfo => {
  const { filters } = state;
  const fieldsToTrim = [
    "returnId",
    "dealerReference",
    "invoiceNumber",
    "nfeNumber",
    "partNumber",
  ] as const;

  const trimmedFilters = Object.fromEntries(
    fieldsToTrim.map((field) => [field, getTrimmedValue(filters[field] || "")])
  );

  return {
    ...filters,
    ...trimmedFilters,
  };
};

const getTrimmedValue = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue || null;
};
